import flatpickr from 'flatpickr';
import { get } from '../utils/request';

import 'flatpickr/dist/themes/light.css';
import './widget.style.css';
import { Instance } from 'flatpickr/dist/types/instance';
import { IntegrationConfig, DBMConfig } from '../interfaces';

type Hotels = { id: string; name: string; description: string; email: string; telephone: string }[];

const MIN_ADULTS = 1;
const MIN_CHILDREN = 0;

const addDay = (day: Date) => day.setDate(day.getDate() + 1);
const PREFIX = 'gldbm-widget__';
const numberOptions = (num: number, initialValue: number): { value: string }[] => {
  const options = [];

  for (let i = initialValue; i <= num; i++) {
    options.push({ value: i.toString() });
  }

  return options;
};

function getHotels(collectionId: string): Promise<Hotels> {
  return get(`https://${process.env.DBM_NEW_URL}/api/hotels/${collectionId}`);
}

const toggleView = (event: Event): void => {
  const active = document.getElementById('gldbm-widget')!.classList.toggle('active');

  if (event && event.target) (<HTMLDivElement>event.target).innerHTML = active ? '›' : '‹';
};

const createDatePicker = (
  name: string,
  labelText: string,
  defaultValue: any,
  onChange?: (selectedDates: Date[], dateStr: string, instance: Instance) => void
) => {
  const container = document.createElement('div');
  const label = document.createElement('label');
  const inputHidden = createInput();
  const inputFlatPicker = createInput();

  function setDateOnInput() {
    //@ts-ignore
    const date = inputFlatPicker._flatpickr.selectedDates[0];

    if (date) {
      //@ts-ignore
      inputHidden.value = inputFlatPicker._flatpickr.formatDate(new Date(date), 'Y-m-d');
    }
  }

  container.className = 'input input--1-2';
  label.className = 'input__label';
  label.htmlFor = PREFIX + name;
  label.innerText = labelText;
  inputHidden.type = 'hidden';
  inputHidden.name = name;
  inputFlatPicker.id = PREFIX + name;
  inputFlatPicker.name = `${name}-flat-picker`;
  flatpickr(inputFlatPicker, {
    appendTo: container,
    disableMobile: true,
    dateFormat: 'd/m/Y',
    minDate: defaultValue,
    defaultDate: defaultValue,
    onChange: (selectedDates, dateStr, instance) => {
      setDateOnInput();
      if (typeof onChange === 'function') {
        onChange(selectedDates, dateStr, instance);
      }
    }
  });
  setDateOnInput();
  label.appendChild(inputHidden);
  label.appendChild(inputFlatPicker);
  container.appendChild(label);

  return container;
};

const createInput = () => {
  const input = document.createElement('input');

  input.className = 'gldbm-input';

  return input;
};

const createSelect = (
  name: string,
  labelText: string,
  options: { text?: string; value: string }[],
  defaultValue?: string
) => {
  const label = document.createElement('label');
  const select = document.createElement('select');

  label.className = 'input__label';
  label.htmlFor = PREFIX + name;
  label.innerText = labelText;
  select.id = PREFIX + name;
  select.name = name;
  select.className = 'gldbm-select';

  options.forEach(option => {
    const optionElement = document.createElement('option');

    optionElement.value = option.value;
    optionElement.innerText = option.text || option.value;
    if (option.value === defaultValue) {
      optionElement.selected = true;
    }

    select.appendChild(optionElement);
  });

  label.appendChild(select);

  return label;
};

export default function(
  integration: IntegrationConfig,
  dbm: DBMConfig,
  collectionId: string,
  hotelId?: string
) {
  // Widget form
  const widget = document.createElement('form');
  widget.id = 'gldbm-widget';
  widget.className = 'gldbm-widget active';
  widget.style.background = integration.widgetBg;
  widget.style.color = integration.widgetColor;
  widget.setAttribute('data-guestline-form', 'true');

  // Title
  const title = document.createElement('div');
  title.className = PREFIX + 'header';
  title.innerText = integration.widgetHeaderText;
  widget.appendChild(title);

  // Hotels select
  const hotelContainer = document.createElement('div');
  widget.appendChild(hotelContainer);

  getHotels(collectionId).then(hotels => {
    const transformedHotels = hotels.map(({ id, name }) => ({ value: id, text: name }));

    if (transformedHotels.length > 1) {
      transformedHotels.unshift({ value: '', text: 'Any hotel' });

      hotelContainer.className = 'input';
      hotelContainer.appendChild(
        createSelect('hotel-id', 'Choose hotel', transformedHotels, hotelId)
      );
    } else {
      let input = createInput();

      input.id = PREFIX + 'hotel-id';
      input.name = 'hotel-id';
      input.type = 'hidden';
      input.value = transformedHotels[0].value;

      hotelContainer.appendChild(input);
    }
  });

  // Add date pickers
  const today = new Date();
  widget.appendChild(
    createDatePicker('arrival', 'Check-in', today, (selectedDates, _dateStr, _instance) => {
      //@ts-ignore
      const checkOutInput = document.getElementById(`${PREFIX}departure`)!._flatpickr;
      const checkIn = new Date(selectedDates[0]);
      const checkOut = new Date(checkOutInput.selectedDates);
      const checkOutMinDate = addDay(checkIn);

      checkOutInput.set('minDate', checkOutMinDate);
      if (checkIn > checkOut) {
        checkOutInput.setDate(checkOutMinDate, true);
      }
    })
  );
  widget.appendChild(createDatePicker('departure', 'Check-out', addDay(today)));

  // Number of guests selects
  const adultsContainer = document.createElement('div');
  adultsContainer.className = 'input input--1-2';
  adultsContainer.appendChild(
    createSelect('adults', 'Adults', numberOptions(dbm.dropdownLength, MIN_ADULTS), '2')
  );
  widget.appendChild(adultsContainer);

  const childrenContainer = document.createElement('div');
  childrenContainer.className = 'input input--1-2';
  childrenContainer.appendChild(
    createSelect(
      'children',
      'Children',
      numberOptions(dbm.dropdownLength, MIN_CHILDREN),
      MIN_CHILDREN.toString()
    )
  );
  widget.appendChild(childrenContainer);

  // Submit button
  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = PREFIX + 'submit';
  submit.style.background = integration.widgetSubmitBg;
  submit.style.color = integration.widgetSubmitColor;
  submit.innerText = 'Check availability';
  widget.appendChild(submit);

  // Toggle
  const toggle = document.createElement('div');
  toggle.className = PREFIX + 'toggle';
  toggle.style.background = integration.widgetBg;
  toggle.style.color = integration.widgetColor;
  toggle.innerHTML = '›';
  toggle.onclick = toggleView;
  widget.appendChild(toggle);

  document.body.appendChild(widget);
}
